import React, { useState, useRef, useEffect } from 'react';
import {
  Panel,
  useReactFlow,
  getViewportForBounds,
} from '@xyflow/react';
import { toPng, toSvg } from 'html-to-image';

// Renders the whole mechanism (including parts scrolled off-screen) to an image.
// We size the export canvas to the graph's own bounds so the aspect ratio is
// preserved and tall trees don't get squished into a fixed landscape frame.
// (For SVG, edges/text stay vector; the molecule structures are still embedded
// as the raster /mol_*.png bitmaps, so SCALE keeps those reasonably crisp.)
const SCALE = 2; // 2x for crisp output on hi-dpi / when zoomed into a paper
const PADDING = 0.1; // 10% margin around the graph
const MAX_DIM = 8000; // guard against absurdly large canvases

// Each entry: which html-to-image renderer to use and the download extension.
const FORMATS = {
  png: { render: toPng, ext: 'png' },
  svg: { render: toSvg, ext: 'svg' },
};

// Some intermediates (e.g. "unnamed_intermediate") have no /mol_*.png on the
// server. The browser tolerates the broken <img>, but html-to-image fetches
// the URL, gets the 404 HTML page back, inlines it as a bogus data:image/png,
// and the failed load rejects the whole export. We swap such images for this
// placeholder before capture and restore them afterwards.
const MISSING_IMG =
  'data:image/svg+xml;charset=utf-8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="150" height="150">' +
      '<rect width="150" height="150" fill="#f0f0f0" stroke="#cccccc"/>' +
      '<text x="75" y="75" font-family="sans-serif" font-size="12" fill="#999999" ' +
      'text-anchor="middle" dominant-baseline="middle">no image</text></svg>',
  );

function triggerDownload(dataUrl, ext) {
  const a = document.createElement('a');
  a.setAttribute('download', `mechanism.${ext}`);
  a.setAttribute('href', dataUrl);
  a.click();
}

function DownloadImageButton() {
  // getNodesBounds from the hook (not the standalone import) carries the node
  // lookup, so it returns correct bounds and doesn't warn about sub flows.
  const { getNodes, getNodesBounds } = useReactFlow();
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // Close the popup when clicking anywhere outside it.
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const exportAs = (event, format) => {
    setOpen(false);
    const { render, ext } = FORMATS[format];

    const nodes = getNodes();
    if (!nodes.length) return;

    // Scope to *this* flow instance rather than a global querySelector, so a
    // page with more than one viewer exports the right graph.
    const flow = event.currentTarget.closest('.react-flow');
    const viewport = flow && flow.querySelector('.react-flow__viewport');
    if (!viewport) return;

    const bounds = getNodesBounds(nodes);
    let imageWidth = Math.round(bounds.width * SCALE * (1 + PADDING * 2));
    let imageHeight = Math.round(bounds.height * SCALE * (1 + PADDING * 2));
    const clamp = Math.min(1, MAX_DIM / imageWidth, MAX_DIM / imageHeight);
    imageWidth = Math.round(imageWidth * clamp);
    imageHeight = Math.round(imageHeight * clamp);

    const tf = getViewportForBounds(bounds, imageWidth, imageHeight, 0.1, 4, PADDING);

    // Swap broken molecule images (missing /mol_*.png -> 404 HTML page) for a
    // placeholder so html-to-image doesn't choke trying to inline them.
    const broken = Array.from(viewport.querySelectorAll('img')).filter(
      (img) => img.complete && img.naturalWidth === 0,
    );
    const originalSrcs = broken.map((img) => img.getAttribute('src'));
    broken.forEach((img) => {
      img.src = MISSING_IMG;
    });

    const restore = () => {
      broken.forEach((img, i) => {
        if (originalSrcs[i] != null) img.setAttribute('src', originalSrcs[i]);
      });
    };

    setBusy(true);
    render(viewport, {
      backgroundColor: '#ffffff',
      width: imageWidth,
      height: imageHeight,
      // Don't try to inline web fonts: our Bootstrap / Font-Awesome CSS is
      // loaded cross-origin from CDNs, so reading their cssRules throws. Text
      // just falls back to the default font in the exported image.
      skipFonts: true,
      // Backstop: if an image genuinely fails to fetch, use the placeholder
      // instead of rejecting the whole export.
      imagePlaceholder: MISSING_IMG,
      style: {
        width: imageWidth,
        height: imageHeight,
        transform: `translate(${tf.x}px, ${tf.y}px) scale(${tf.zoom})`,
      },
    })
      .then((dataUrl) => triggerDownload(dataUrl, ext))
      .catch((err) => console.error('Mechanism image export failed:', err))
      .finally(() => {
        restore();
        setBusy(false);
      });
  };

  return (
    <Panel position="top-right">
      <div className="download-image-wrapper" ref={containerRef}>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          disabled={busy}
          title="Download mechanism as an image"
          className="download-image-button"
        >
          {busy ? 'Exporting…' : '⬇ Image ▾'}
        </button>
        {open && !busy && (
          <div className="download-image-menu">
            <button
              type="button"
              className="download-image-option"
              onClick={(e) => exportAs(e, 'png')}
            >
              PNG <span className="download-image-hint">raster</span>
            </button>
            <button
              type="button"
              className="download-image-option"
              onClick={(e) => exportAs(e, 'svg')}
            >
              SVG <span className="download-image-hint">vector</span>
            </button>
          </div>
        )}
      </div>
    </Panel>
  );
}

export default DownloadImageButton;
